/**
 * @file RequestDomainDialog.tsx
 * @description «Попросить подключить свой домен» в кабинете магазина
 * (phase19.28 этап А; PL11): адрес (очистка ввода и подсказка — общая
 * `normalizeHost`, как в кабинете платформы), комментарий, пояснение. IP
 * платформы магазину не показываем — платформа сообщит его в ответе на
 * заявку. Отказы: 422 адрес, 409 занят/уже подключён, 429 частые заявки —
 * текстом под полем.
 */

import { normalizeHost, NORMALIZE_HOST_ERROR_MESSAGES } from "@chaiberi/shared";
import { useState } from "react";

import { ApiError } from "../../lib/api-client";
import { shopDomains } from "../../lib/api-shop-domains";
import { hostPreview } from "../../lib/platform/shop-domain-status";
import { shopDomainError } from "../../lib/platform/shop-errors";
import { ShopTextField } from "../platform/shops/ShopTextField";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { Textarea } from "../ui/textarea";

/** Предел комментария к заявке. */
const COMMENT_MAX = 500;

/** Отказ заявки → текст под полем: коды адреса — общие с платформой, остальные — текст сервера. */
function requestErrorText(err: unknown): string {
  if (err instanceof ApiError && (err.code === "shop_domain_invalid" || err.code === "shop_domain_taken")) return shopDomainError(err);
  if (err instanceof ApiError && err.message) return err.message;
  return "Не удалось отправить заявку. Повторите попытку";
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}

export function RequestDomainDialog({ open, onClose, onSent }: Props) {
  const notify = useNotify();
  const [input, setInput] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const parsed = input.trim() ? normalizeHost(input, { kind: "CUSTOM" }) : null;
  const hint = parsed?.data ? `Заявка на: ${hostPreview(parsed.data)}` : "Например, lavka.ru — можно вставить адрес из браузера";

  function close() {
    setInput("");
    setComment("");
    setError(null);
    onClose();
  }

  async function submit() {
    if (!parsed || parsed.error) return setError(parsed?.error ? NORMALIZE_HOST_ERROR_MESSAGES[parsed.error] : "Укажите адрес сайта.");
    setBusy(true);
    setError(null);
    try {
      const result = await shopDomains.request({ host: parsed.data, ...(comment.trim() ? { comment: comment.trim() } : {}) });
      notify.success(result.sentToPlatform ? "Заявка отправлена" : "Заявка записана, платформа увидит её в карточке магазина");
      close();
      onSent();
    } catch (err) {
      setError(requestErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && close()}>
      <DialogContent fullScreen className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Подключить свой домен</DialogTitle>
          <DialogDescription>
            Платформа подключит домен и напишет вам. A-запись домена должна указывать на IP платформы — его сообщат в ответе.
          </DialogDescription>
        </DialogHeader>
        <form id="request-domain-form" noValidate className="flex flex-col gap-4" onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <ShopTextField
            id="request-domain-host"
            label="Домен"
            required
            value={input}
            onChange={(value) => {
              setInput(value);
              setError(null);
            }}
            error={error ?? undefined}
            hint={hint}
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="request-domain-comment">Комментарий</Label>
            <Textarea
              id="request-domain-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={COMMENT_MAX}
              placeholder="Необязательно: где зарегистрирован домен, кому писать"
              disabled={busy}
            />
          </div>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Отмена
          </Button>
          <Button type="submit" form="request-domain-form" loading={busy}>
            Отправить заявку
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
